import { useState, useEffect, useCallback, useRef } from "react";
import api from "../services/api";

const STRATEGIES = [
  { value: "merge",        label: "Fusion intelligente",    desc: "Importe les écritures non présentes, conserve vos données existantes" },
  { value: "history_only", label: "Historique uniquement",  desc: "Importe uniquement les écritures antérieures à la date de démarrage" },
  { value: "clean",        label: "Remplacement complet",   desc: "Supprime les écritures FEC existantes et réimporte tout ⚠️" },
];

const TVA_LABELS = {
  mensuel: "Déclaration mensuelle", trimestriel: "Déclaration trimestrielle",
  annuel:  "Déclaration annuelle", franchise: "Franchise TVA art. 293B",
};

const DEFAULT_ACCOUNTS = [
  { account_code: "101", account_name: "Capital",            side: "credit" },
  { account_code: "110", account_name: "Report à nouveau",   side: "credit" },
  { account_code: "401", account_name: "Fournisseurs",       side: "credit" },
  { account_code: "411", account_name: "Clients",            side: "debit"  },
  { account_code: "44551","account_name":"TVA à décaisser",  side: "credit" },
  { account_code: "44566","account_name":"TVA déductible",   side: "debit"  },
  { account_code: "512", account_name: "Banque",             side: "debit"  },
  { account_code: "530", account_name: "Caisse",             side: "debit"  },
  { account_code: "164", account_name: "Emprunts",           side: "credit" },
  { account_code: "201", account_name: "Immobilisations",    side: "debit"  },
];

function fmt(n) { return (n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }); }
function fmtD(d) { return d ? new Date(d).toLocaleDateString("fr-FR") : "—"; }

export default function FEC({ currentCompanyId, currentCompany }) {
  const [tab, setTab] = useState("dashboard");

  // Dashboard
  const [status, setStatus]       = useState(null);
  const [imports, setImports]     = useState([]);
  const [statusLoading, setSL]    = useState(false);

  // Import
  const [file, setFile]           = useState(null);
  const [fileContent, setFC]      = useState(null);
  const [analysis, setAnalysis]   = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [strategy, setStrategy]   = useState("merge");
  const [dateStart, setDateStart] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setIR]     = useState(null);
  const [importError, setIE]      = useState("");
  const [importPreview, setIP]    = useState(null); // aperçu : à supprimer / à insérer / refus
  const dropRef                   = useRef(null);
  // L'aperçu ne vaut que pour le fichier, la stratégie et la date choisis
  useEffect(() => { setIP(null); }, [fileContent, strategy, dateStart]);

  // Opening balances
  const [balances, setBalances]   = useState(DEFAULT_ACCOUNTS.map(a => ({ ...a, debit: 0, credit: 0 })));
  const [balSaved, setBalSaved]   = useState(false);
  const [balDate, setBalDate]     = useState(`${new Date().getFullYear()}-01-01`);
  const [existingBal, setExBal]   = useState([]);

  // Export
  const [exportYear, setEY]       = useState(String(new Date().getFullYear()));
  const [exportingFec, setExportingFec] = useState(false);

  // Export FEC authentifié — remplace l'ancien <a href={api.fecAdvanced.exportUrl(...)}>
  // qui ne transmettait jamais le token JWT (401 systématique, cf. audit bug #1).
  const handleExportFEC = async () => {
    if (!currentCompanyId) return;
    setExportingFec(true);
    try {
      const { blob, filename } = await api.fec.exportDownload(currentCompanyId, exportYear);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement("a"), { href: url, download: filename }).click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message);
    } finally {
      setExportingFec(false);
    }
  };

  const loadStatus = useCallback(() => {
    if (!currentCompanyId) return;
    setSL(true);
    Promise.all([
      api.fecAdvanced.status(currentCompanyId),
      api.fecAdvanced.imports(currentCompanyId),
    ]).then(([s, imp]) => {
      setStatus(s);
      setImports(imp || []);
    }).catch(console.error).finally(() => setSL(false));
  }, [currentCompanyId]);

  const loadBalances = useCallback(() => {
    if (!currentCompanyId) return;
    api.onboarding.openingBalances(currentCompanyId)
      .then(rows => {
        if (rows.length > 0) {
          setExBal(rows);
          const byCode = {};
          rows.forEach(r => { byCode[r.account_code] = r; });
          setBalances(DEFAULT_ACCOUNTS.map(a => ({
            ...a,
            debit:  byCode[a.account_code]?.debit  || 0,
            credit: byCode[a.account_code]?.credit || 0,
          })));
          if (rows[0]?.balance_date) setBalDate(rows[0].balance_date);
        }
      }).catch(() => {});
  }, [currentCompanyId]);

  useEffect(() => {
    if (tab === "dashboard") loadStatus();
    if (tab === "balances")  loadBalances();
  }, [tab, currentCompanyId, loadStatus, loadBalances]);

  // ── Drag & drop ─────────────────────────────────────────────
  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (f) loadFile(f);
  };

  const loadFile = (f) => {
    setFile(f); setAnalysis(null); setIR(null); setIE("");
    const reader = new FileReader();
    reader.onload = (e) => setFC(e.target.result);
    reader.readAsText(f, "utf-8");
  };

  const handleAnalyze = async () => {
    if (!fileContent || !currentCompanyId) return;
    setAnalyzing(true); setAnalysis(null); setIE("");
    try {
      const res = await api.fecAdvanced.analyze(currentCompanyId, { content: fileContent });
      setAnalysis(res);
    } catch (e) { setIE(e.message); }
    finally { setAnalyzing(false); }
  };

  // Aperçu de l'import : ce qui serait supprimé puis inséré, ou le motif du refus (rien n'est modifié)
  const handleImportPreview = async () => {
    if (!fileContent || !currentCompanyId) return;
    setImporting(true); setIP(null); setIE("");
    try {
      setIP(await api.fecAdvanced.importPreview(currentCompanyId, { content: fileContent, strategy, date_start: dateStart || null }));
    } catch (e) { setIE(e.message); }
    finally { setImporting(false); }
  };

  const handleImport = async () => {
    if (!fileContent || !currentCompanyId || !importPreview?.allowed) return;
    setImporting(true); setIR(null); setIE("");
    try {
      const res = await api.fecAdvanced.smartImport(currentCompanyId, {
        content: fileContent, strategy, date_start: dateStart || null,
        filename: file?.name || "import.txt",
      });
      setIR(res);
      setFile(null); setFC(null); setAnalysis(null); setIP(null);
      loadStatus();
    } catch (e) { setIE(e.message); }
    finally { setImporting(false); }
  };

  const handleSaveBalances = async () => {
    if (!currentCompanyId) return;
    try {
      await api.onboarding.saveOpeningBalances(currentCompanyId, {
        balances, balance_date: balDate,
      });
      setBalSaved(true);
      setTimeout(() => setBalSaved(false), 3000);
      loadBalances();
    } catch (e) { alert(e.message); }
  };

  const totalDebit  = balances.reduce((s, b) => s + (parseFloat(b.debit)  || 0), 0);
  const totalCredit = balances.reduce((s, b) => s + (parseFloat(b.credit) || 0), 0);
  const balanced    = Math.abs(totalDebit - totalCredit) < 0.01;

  const TABS = [
    { id: "dashboard", label: "Tableau de bord" },
    { id: "import",    label: "Importer un FEC" },
    { id: "export",    label: "Exporter" },
    { id: "balances",  label: "Soldes d'ouverture" },
    { id: "guide",     label: "Démarrer sans FEC" },
  ];

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">FEC & Exports comptables</h2>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, borderBottom: "var(--border)", marginBottom: 20, overflowX: "auto" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "9px 18px", background: "none", border: "none", cursor: "pointer",
            fontSize: 13, fontWeight: tab === t.id ? 700 : 400,
            color: tab === t.id ? "var(--g)" : "var(--text-muted)",
            borderBottom: `2px solid ${tab === t.id ? "var(--g)" : "transparent"}`,
            marginBottom: -1, whiteSpace: "nowrap",
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── TAB 1 : TABLEAU DE BORD ─────────────────────────── */}
      {tab === "dashboard" && (
        <div>
          {statusLoading && <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>Chargement…</div>}
          {status && !statusLoading && (
            <>
              {/* Indicateur statut */}
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                  <div style={{ fontSize: 40, lineHeight: 1 }}>
                    {status.indicator === "green" ? "🟢" : status.indicator === "orange" ? "🟠" : "🔴"}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{status.message}</div>
                    {status.stats?.total_entries > 0 && (
                      <div style={{ fontSize: 12, color: "var(--text-muted)", display: "flex", gap: 20, flexWrap: "wrap", marginTop: 6 }}>
                        <span>📅 Période : {fmtD(status.stats.first_entry)} → {fmtD(status.stats.last_entry)}</span>
                        <span>📝 {fmt(status.stats.total_entries)} écritures</span>
                        <span>🤝 {fmt(status.stats.fec_entries)} via FEC · {fmt(status.stats.manual_entries)} saisies</span>
                      </div>
                    )}
                  </div>
                  {status.completeness === "none" && (
                    <button className="btn primary" onClick={() => setTab("import")}>Importer un FEC</button>
                  )}
                </div>
              </div>

              {/* KPIs */}
              {status.stats?.total_entries > 0 && (
                <div className="kpi-grid k4" style={{ marginBottom: 16 }}>
                  <div className="kpi"><div className="kpi-label">Total écritures</div><div className="kpi-value">{fmt(status.stats.total_entries)}</div></div>
                  <div className="kpi"><div className="kpi-label">Total débit</div><div className="kpi-value" style={{ color: "var(--red)" }}>{fmt(status.stats.total_debit)} €</div></div>
                  <div className="kpi"><div className="kpi-label">Total crédit</div><div className="kpi-value" style={{ color: "var(--g)" }}>{fmt(status.stats.total_credit)} €</div></div>
                  <div className="kpi"><div className="kpi-label">Équilibre</div><div className="kpi-value" style={{ color: Math.abs((status.stats.total_debit||0) - (status.stats.total_credit||0)) < 1 ? "var(--g)" : "var(--red)" }}>{Math.abs((status.stats.total_debit||0) - (status.stats.total_credit||0)) < 1 ? "✓ Équilibré" : "⚠ Déséquilibré"}</div></div>
                </div>
              )}

              {/* Paramètres comptables */}
              {status.settings && (
                <div className="card" style={{ marginBottom: 16 }}>
                  <div className="card-header"><span className="card-title">⚙️ Paramètres comptables</span></div>
                  <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13 }}>
                    {status.settings.start_date && <div><span style={{ color: "var(--text-muted)" }}>Démarrage : </span><strong>{fmtD(status.settings.start_date)}</strong></div>}
                    {status.settings.fiscal_year_start && <div><span style={{ color: "var(--text-muted)" }}>Exercice : </span><strong>{fmtD(status.settings.fiscal_year_start)} → {fmtD(status.settings.fiscal_year_end)}</strong></div>}
                    {status.settings.tva_regime && <div><span style={{ color: "var(--text-muted)" }}>Régime TVA : </span><strong>{TVA_LABELS[status.settings.tva_regime] || status.settings.tva_regime}</strong></div>}
                    <div><span style={{ color: "var(--text-muted)" }}>Soldes d'ouverture : </span><strong style={{ color: status.hasOpeningBalances ? "var(--g)" : "var(--amber)" }}>{status.hasOpeningBalances ? "✓ Saisis" : "À saisir"}</strong></div>
                  </div>
                </div>
              )}

              {/* Historique des imports */}
              {imports.length > 0 && (
                <div className="card">
                  <div className="card-header"><span className="card-title">📋 Historique des imports FEC</span></div>
                  <div className="tbl-overflow">
                    <table className="tbl">
                      <thead><tr><th>Date</th><th>Fichier</th><th>Stratégie</th><th>Période</th><th>Importées</th><th>Ignorées</th><th>Statut</th></tr></thead>
                      <tbody>
                        {imports.map(imp => (
                          <tr key={imp.id}>
                            <td style={{ color: "var(--text-muted)", fontSize: 12 }}>{fmtD(imp.created_at)}</td>
                            <td style={{ fontFamily: "monospace", fontSize: 11 }}>{imp.filename || "—"}</td>
                            <td><span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 10, background: "#185fa520", color: "#185fa5" }}>{imp.strategy}</span></td>
                            <td style={{ fontSize: 12 }}>{fmtD(imp.period_from)} → {fmtD(imp.period_to)}</td>
                            <td style={{ fontWeight: 700, color: "var(--g)" }}>{fmt(imp.lines_imported)}</td>
                            <td style={{ color: "var(--text-muted)" }}>{fmt(imp.lines_skipped)}</td>
                            <td><span className={`badge ${imp.status === "completed" ? "success" : "danger"}`}>{imp.status === "completed" ? "Terminé" : imp.status}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!status.stats?.total_entries && (
                <div className="card">
                  <div style={{ textAlign: "center", padding: "32px 0" }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>📂</div>
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>Aucune écriture comptable</div>
                    <div style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 20 }}>
                      Importez un FEC pour commencer, ou utilisez directement les modules de facturation.
                    </div>
                    <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                      <button className="btn primary" onClick={() => setTab("import")}>📂 Importer un FEC</button>
                      <button className="btn" onClick={() => setTab("guide")}>✨ Démarrer sans FEC</button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── TAB 2 : IMPORTER ─────────────────────────────────── */}
      {tab === "import" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {!file && !importResult && (
            <div className="card">
              <div className="card-header"><span className="card-title">📂 Déposer votre FEC</span></div>
              <div
                ref={dropRef}
                onDragOver={e => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => document.getElementById("fec-file-input").click()}
                style={{
                  border: "2px dashed var(--border)", borderRadius: 12, padding: 40,
                  textAlign: "center", cursor: "pointer", transition: "border-color 0.2s",
                  background: "#fafafa",
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = "var(--g)"}
                onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
              >
                <div style={{ fontSize: 36, marginBottom: 10 }}>📁</div>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Glisser-déposer votre FEC ici</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Format .txt · Séparateur | · Norme DGFiP · Exercices 2020–2026</div>
                <input id="fec-file-input" type="file" accept=".txt,.csv" onChange={e => loadFile(e.target.files[0])} style={{ display: "none" }} />
              </div>
            </div>
          )}

          {file && !importResult && (
            <>
              {/* Fichier chargé */}
              <div className="card">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 24 }}>📄</span>
                    <div>
                      <div style={{ fontWeight: 700 }}>{file.name}</div>
                      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{(file.size / 1024).toFixed(1)} Ko</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn" onClick={() => { setFile(null); setFC(null); setAnalysis(null); setIE(""); }}>✕ Retirer</button>
                    <button className="btn primary" onClick={handleAnalyze} disabled={analyzing}>
                      {analyzing ? "Analyse…" : "🔍 Analyser d'abord"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Rapport d'analyse */}
              {analysis && (
                <div className="card">
                  <div className="card-header"><span className="card-title">📊 Rapport d'analyse</span></div>
                  <div className="kpi-grid k4" style={{ marginBottom: 16 }}>
                    <div className="kpi"><div className="kpi-label">Écritures</div><div className="kpi-value">{fmt(analysis.lines)}</div></div>
                    <div className="kpi"><div className="kpi-label">Comptes</div><div className="kpi-value">{analysis.accounts}</div></div>
                    <div className="kpi"><div className="kpi-label">Total débit</div><div className="kpi-value" style={{ fontSize: 14 }}>{fmt(analysis.total_debit)} €</div></div>
                    <div className="kpi"><div className="kpi-label">Équilibre</div><div className="kpi-value" style={{ color: analysis.balanced ? "var(--g)" : "var(--red)" }}>{analysis.balanced ? "✓ OK" : "⚠ Déséq."}</div></div>
                  </div>
                  <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13, marginBottom: 12 }}>
                    <div><span style={{ color: "var(--text-muted)" }}>Période : </span><strong>{fmtD(analysis.period?.from)} → {fmtD(analysis.period?.to)}</strong></div>
                    <div><span style={{ color: "var(--text-muted)" }}>Type : </span><strong>{{full_year: "Exercice complet", partial: "Exercice partiel", multi_year: "Multi-exercices"}[analysis.type]}</strong></div>
                    <div><span style={{ color: "var(--text-muted)" }}>Journaux : </span><strong>{analysis.journals?.join(", ")}</strong></div>
                  </div>

                  {/* Conflits */}
                  {analysis.conflicts?.has_conflicts && (
                    <div style={{ padding: "12px 14px", background: "#ba751720", border: "1px solid #ba751740", borderRadius: 8, marginBottom: 12 }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, color: "var(--amber)" }}>⚠️ Conflits détectés</div>
                      {analysis.conflicts.transactions_in_period > 0 && (
                        <div style={{ fontSize: 12, marginBottom: 3 }}>📝 {fmt(analysis.conflicts.transactions_in_period)} écritures existantes sur la même période</div>
                      )}
                      {analysis.conflicts.invoices_in_period > 0 && (
                        <div style={{ fontSize: 12 }}>📄 {fmt(analysis.conflicts.invoices_in_period)} factures sur la même période</div>
                      )}
                    </div>
                  )}

                  {analysis.total_errors > 0 && (
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      ⚠ {analysis.total_errors} ligne(s) non parsable(s) dans le fichier.
                    </div>
                  )}
                </div>
              )}

              {/* Stratégie d'import */}
              <div className="card">
                <div className="card-header"><span className="card-title">⚙️ Stratégie d'import</span></div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                  {STRATEGIES.map(s => (
                    <div key={s.value} onClick={() => setStrategy(s.value)} style={{
                      padding: "12px 14px", borderRadius: 8, cursor: "pointer",
                      border: `1.5px solid ${strategy === s.value ? "var(--g)" : "var(--border)"}`,
                      background: strategy === s.value ? "rgba(26,158,114,0.05)" : "#fff",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${strategy === s.value ? "var(--g)" : "#ccc"}`, background: strategy === s.value ? "var(--g)" : "transparent", flexShrink: 0 }} />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{s.label}</div>
                          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{s.desc}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {strategy === "history_only" && (
                  <div className="form-group">
                    <label className="form-label">Date de démarrage (écritures antérieures importées uniquement)</label>
                    <input className="form-control" type="date" value={dateStart} onChange={e => setDateStart(e.target.value)} />
                  </div>
                )}

                {strategy === "clean" && (
                  <div style={{ padding: "10px 14px", background: "#dc262610", border: "1px solid #dc262630", borderRadius: 8, marginBottom: 12, fontSize: 12, color: "#dc2626" }}>
                    ⚠️ Cette stratégie supprime les écritures issues d'un FEC sur la période du fichier (jamais les écritures saisies ou générées dans ExecPro360), puis réimporte. Refusée si une écriture à supprimer est lettrée ou dans une période clôturée ou verrouillée.
                  </div>
                )}

                {ieError(importError)}

                {/* Aperçu obligatoire avant l'import */}
                {importPreview && (
                  <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontSize: 12.5,
                    background: importPreview.allowed ? "#f0fdf4" : "#fef2f2", border: `1px solid ${importPreview.allowed ? "#86efac" : "#fca5a5"}` }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>Aperçu de l'import — rien n'est encore modifié</div>
                    {importPreview.to_delete && (
                      <div>Supprimées (écritures issues d'un FEC) : <strong>{fmt(importPreview.to_delete.count)}</strong>
                        {importPreview.to_delete.count > 0 && <> du {fmtD(importPreview.to_delete.from)} au {fmtD(importPreview.to_delete.to)} ({Object.entries(importPreview.to_delete.journals).map(([j, n]) => `${j} ${n}`).join(", ")})</>}
                      </div>
                    )}
                    <div>Insérées : <strong>{fmt(importPreview.to_insert)}</strong> ligne(s) sur {fmt(importPreview.lines_total)} ({fmtD(importPreview.period?.from)} → {fmtD(importPreview.period?.to)})</div>
                    {importPreview.duplicates > 0 && <div>Ignorées, déjà présentes : {fmt(importPreview.duplicates)}</div>}
                    {importPreview.ignored_history > 0 && <div>Ignorées, après la date de démarrage : {fmt(importPreview.ignored_history)}</div>}
                    {importPreview.tiers?.lines > 0 && (
                      <div>Clients et fournisseurs : {fmt(importPreview.tiers.lines)} ligne(s) portées sur le compte de leur tiers (compte auxiliaire du FEC)
                        {importPreview.tiers.accounts_to_create.length > 0 && <> · comptes créés : {importPreview.tiers.accounts_to_create.map(a => `${a.code} (${a.name})`).join(", ")}</>}
                      </div>
                    )}
                    {importPreview.tiers?.aux_ignored > 0 && <div style={{ color: "#92400e" }}>⚠ {fmt(importPreview.tiers.aux_ignored)} ligne(s) avec un compte auxiliaire inutilisable : laissée(s) sur le compte collectif</div>}
                    {importPreview.blocking.map((b, i) => <div key={i} style={{ color: "#991b1b", marginTop: 6 }}>⛔ {b}</div>)}
                  </div>
                )}

                {!importPreview || !importPreview.allowed ? (
                  <button className="btn primary" style={{ width: "100%" }} onClick={handleImportPreview} disabled={importing || (!analysis && !fileContent)}>
                    {importing ? "Préparation de l'aperçu…" : "🔍 Aperçu de l'import"}
                  </button>
                ) : (
                  <button className="btn primary" style={{ width: "100%" }} onClick={handleImport} disabled={importing}>
                    {importing ? "Import en cours…" : `📥 Confirmer l'import (${fmt(importPreview.to_insert)} ligne(s)${importPreview.to_delete ? `, ${fmt(importPreview.to_delete.count)} supprimée(s)` : ""})`}
                  </button>
                )}
              </div>
            </>
          )}

          {/* Résultat import */}
          {importResult && (
            <div className="card">
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Import terminé avec succès</div>
                <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>{importResult.message}</div>
              </div>
              <div className="kpi-grid k4" style={{ marginBottom: 16 }}>
                <div className="kpi"><div className="kpi-label">Importées</div><div className="kpi-value" style={{ color: "var(--g)" }}>{fmt(importResult.imported)}</div></div>
                <div className="kpi"><div className="kpi-label">Ignorées</div><div className="kpi-value">{fmt(importResult.skipped)}</div></div>
                <div className="kpi"><div className="kpi-label">Erreurs</div><div className="kpi-value" style={{ color: importResult.errors > 0 ? "var(--red)" : "inherit" }}>{importResult.errors}</div></div>
                <div className="kpi"><div className="kpi-label">Équilibre</div><div className="kpi-value" style={{ color: importResult.balanced ? "var(--g)" : "var(--amber)" }}>{importResult.balanced ? "✓" : "⚠"}</div></div>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                <button className="btn" onClick={() => { setIR(null); setIE(""); }}>Importer un autre fichier</button>
                <button className="btn primary" onClick={() => setTab("dashboard")}>Voir le tableau de bord</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TAB 3 : EXPORTER ────────────────────────────────── */}
      {tab === "export" && (
        <div className="g2">
          <div className="card">
            <div className="card-header"><span className="card-title">⬇️ Exporter un FEC DGFiP</span></div>
            <div className="form-group">
              <label className="form-label">Société</label>
              <input className="form-control" value={currentCompany?.name || "—"} readOnly />
            </div>
            <div className="form-group">
              <label className="form-label">Exercice fiscal</label>
              <select className="form-control" value={exportYear} onChange={e => setEY(e.target.value)}>
                {[0, 1, 2, 3, 4].map(i => {
                  const y = new Date().getFullYear() - i;
                  return <option key={y} value={y}>{y}</option>;
                })}
              </select>
            </div>
            <button
              className="btn primary" style={{ width: "100%" }}
              disabled={!currentCompanyId || exportingFec}
              onClick={handleExportFEC}
            >
              {exportingFec ? "⏳ Génération…" : "⬇️ Générer et télécharger le FEC"}
            </button>
            <div style={{ marginTop: 12, fontSize: 11, color: "var(--text-muted)" }}>
              Fichier nommé selon la norme DGFiP : SIREN_FEC_AAAAMMJJ.txt
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">📋 Validation DGFiP</span></div>
            <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
              Vérifiez la conformité de votre FEC avant envoi à l'administration fiscale.
            </p>
            <button className="btn primary" style={{ width: "100%" }} onClick={async () => {
              if (!currentCompanyId) return;
              try {
                const v = await api.fecAdvanced.validate(currentCompanyId, exportYear);
                alert(`Conformité ${exportYear} : ${v.conformity_score}\n${v.checks.map(c => `${c.ok ? "✅" : "❌"} ${c.label}`).join("\n")}`);
              } catch (e) { alert(e.message); }
            }}>
              🔍 Valider la conformité {exportYear}
            </button>
          </div>
        </div>
      )}

      {/* ── TAB 4 : SOLDES D'OUVERTURE ──────────────────────── */}
      {tab === "balances" && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <span className="card-title">📊 Soldes d'ouverture (À-nouveaux)</span>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <label className="form-label" style={{ margin: 0, fontSize: 12 }}>Date d'ouverture :</label>
                <input className="form-control" type="date" value={balDate} onChange={e => setBalDate(e.target.value)} style={{ width: 150 }} />
              </div>
            </div>
            <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>
              Saisissez les soldes de chaque compte au {fmtD(balDate)} pour démarrer avec une base comptable cohérente.
            </p>

            <table className="tbl" style={{ marginBottom: 12 }}>
              <thead>
                <tr>
                  <th>Compte</th><th>Intitulé</th>
                  <th style={{ textAlign: "right" }}>Débit (€)</th>
                  <th style={{ textAlign: "right" }}>Crédit (€)</th>
                </tr>
              </thead>
              <tbody>
                {balances.map((b, i) => (
                  <tr key={b.account_code}>
                    <td style={{ fontFamily: "monospace", fontWeight: 700, color: "var(--blue)" }}>{b.account_code}</td>
                    <td style={{ fontSize: 13 }}>{b.account_name}</td>
                    <td>
                      <input
                        type="number" step="0.01" min="0"
                        value={b.debit || ""}
                        onChange={e => setBalances(bs => bs.map((x, j) => j === i ? { ...x, debit: parseFloat(e.target.value) || 0 } : x))}
                        style={{ width: "100%", padding: "4px 8px", border: "var(--border)", borderRadius: 6, textAlign: "right", fontSize: 13, background: b.side === "debit" ? "#f0fdf4" : "inherit" }}
                        placeholder="0,00"
                      />
                    </td>
                    <td>
                      <input
                        type="number" step="0.01" min="0"
                        value={b.credit || ""}
                        onChange={e => setBalances(bs => bs.map((x, j) => j === i ? { ...x, credit: parseFloat(e.target.value) || 0 } : x))}
                        style={{ width: "100%", padding: "4px 8px", border: "var(--border)", borderRadius: 6, textAlign: "right", fontSize: 13, background: b.side === "credit" ? "#f0fdf4" : "inherit" }}
                        placeholder="0,00"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: balanced ? "#f0fdf4" : "#fef9ec" }}>
                  <td colSpan={2} style={{ fontWeight: 700, padding: "8px 6px" }}>TOTAUX</td>
                  <td style={{ textAlign: "right", fontWeight: 700, color: "var(--red)", padding: "8px 6px" }}>{fmt(totalDebit)} €</td>
                  <td style={{ textAlign: "right", fontWeight: 700, color: "var(--g)", padding: "8px 6px" }}>{fmt(totalCredit)} €</td>
                </tr>
              </tfoot>
            </table>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
              <div style={{ fontSize: 13 }}>
                {balanced
                  ? <span style={{ color: "var(--g)", fontWeight: 600 }}>✓ Bilan équilibré — Actif = Passif</span>
                  : <span style={{ color: "var(--amber)", fontWeight: 600 }}>⚠ Écart : {fmt(Math.abs(totalDebit - totalCredit))} € — Actif ≠ Passif</span>}
              </div>
              <button className="btn primary" onClick={handleSaveBalances} disabled={balSaved}>
                {balSaved ? "✓ Soldes enregistrés" : "💾 Enregistrer les soldes"}
              </button>
            </div>
          </div>

          {existingBal.length > 0 && (
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Dernière mise à jour : {fmtD(existingBal[0]?.created_at)} · {existingBal.length} compte(s) enregistré(s)
            </div>
          )}
        </div>
      )}

      {/* ── TAB 5 : GUIDE DÉMARRAGE ─────────────────────────── */}
      {tab === "guide" && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ padding: "16px 0", borderBottom: "var(--border)", marginBottom: 20 }}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>✨ Démarrer sans historique comptable</div>
              <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>
                Vous pouvez utiliser toutes les fonctionnalités de FinanceApp dès maintenant.
                Votre historique comptable pourra être importé à tout moment via <strong>Importer un FEC</strong>.
                Vos données actuelles seront préservées lors de l'import.
              </p>
            </div>

            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 12 }}>Checklist de démarrage</div>
            {[
              { icon: "🏢", label: "Société configurée",       sub: "SIREN, adresse, TVA, forme juridique",      done: !!currentCompany?.siret },
              { icon: "📊", label: "Soldes d'ouverture saisis", sub: "Banque, Clients, Fournisseurs, Capital",   done: status?.hasOpeningBalances },
              { icon: "📧", label: "Premier client créé",       sub: "Ajoutez vos clients depuis Facturation",   done: false },
              { icon: "📄", label: "Première facture émise",    sub: "Testez le module Facturation",              done: false },
              { icon: "🏦", label: "Compte bancaire renseigné", sub: "IBAN ou solde saisi manuellement",         done: false },
              { icon: "👔", label: "Expert-comptable invité",   sub: "Optionnel — depuis Accès expert-comptable", done: false },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 0", borderBottom: i < 5 ? "0.5px solid var(--border)" : "none" }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                  background: item.done ? "var(--g)" : "#f3f4f6",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: item.done ? 14 : 16,
                  color: item.done ? "#fff" : "inherit",
                }}>
                  {item.done ? "✓" : item.icon}
                </div>
                <div>
                  <div style={{ fontWeight: item.done ? 600 : 400, color: item.done ? "var(--text-muted)" : "inherit", textDecoration: item.done ? "line-through" : "none" }}>{item.label}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{item.sub}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">📂 Importer votre FEC plus tard</span></div>
            <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
              Quand vous serez prêt à importer votre historique comptable, FinanceApp propose trois stratégies :
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              {STRATEGIES.map(s => (
                <div key={s.value} style={{ display: "flex", gap: 10, padding: "10px 14px", background: "#f9fafb", borderRadius: 8 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{s.label}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <button className="btn primary" onClick={() => setTab("import")}>
              📂 Aller à l'import FEC
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ieError(msg) {
  if (!msg) return null;
  return (
    <div style={{ padding: "10px 14px", background: "#dc262610", border: "1px solid #dc262630", borderRadius: 8, marginBottom: 12, fontSize: 13, color: "#dc2626" }}>
      {msg}
    </div>
  );
}
